/*
XXX_JSREFACTOR_XXX
todo @JSREFACTOR
    - Refactor to clearly separate the validation from the UI and styling.
*/
import { gtm } from 'root/utils/tracking/googleTagManagerDeprecated';
import { CookieMonster } from 'root/utils/cookies/cookieMonster';
import formConstants from 'root/global_configs/formConstants.json';
import googlePlacesFieldAutocomplete from 'root/helpers/googlePlacesFieldAutocomplete';
import { getFieldName } from 'root/utils/forms/getFieldData';
import { initPhoneLib } from 'root/main/components/forms/phoneInit';
import { getAnonymizedFieldValue } from 'root/utils/forms/getAnonymizedFieldValue';
import {
    loadCaptcha,
    recaptchaFormFieldValidation,
} from 'root/core/tracking/recaptcha/recaptcha';
import { handleDomainPrefixField } from 'root/helpers/formHelpers';

import {
    getDecodedFormFieldValue,
    isFormExcludedFromLocalStorage,
    isEnabledFormAutofill,
} from 'root/utils/dom/localStorage';
import { LoggingHandler } from 'root/utils/errorReporting/LoggingHandler';
import honeyPotField from 'root/main/components/forms/ui/honeyPotField';
import { GoogleTagManager } from 'root/utils/tracking/GA4/googleTagManager';
import { FormGTMObjectConstructor } from 'root/utils/tracking/GA4/formTracking';
import { handleZipCodeField } from 'root/helpers/formHelpers';
import doFetch from 'root/utils/cors/doFetch';

export default class FormUI {
    constructor(el) {
        this.form = el;
        this.isChatOpen = false;
        this.useEmailDomainValidation =
            this.form.dataset.restaurantLogin !== 'forms-login' &&
            window.location.pathname !==
                '/pos/restaurant/register-for-a-free-trial-sales/';
        this.cm = new CookieMonster();
        this.submitButton = this.form.querySelector('[data-formui=button]');
        this.inputErrorClass = 'js-input-error';
        this.validInputFieldClass = 'js-field-valid';
        this.invalidInputFieldClass = 'js-field-invalid';
        this.showErrorMsgClass = 'js-label-message-error';
        this.errorMsgSelector = '[data-form=error]';
        this.emailDomainErrorMsgSelector = '[data-form="domain-error"]';
        this.unavailableDomainMsg = this.form.querySelector(
            '[data-form=unavailable-domain-error]'
        );
        this.inputParentSelector = '[data-formfield]';
        this.inputSelector = '.gb-form__field-text,.gb-checkbox__field[required]';
        this.hiddenFormClass = 'js-hidden';
        this.phoneLibInstance = '';
        this.fieldsValidationEvent = new CustomEvent('formFieldsValid', {
            bubbles: true,
            cancelable: true,
            composed: false,
        });
        this.recaptchaContainer = this.form.querySelector(
            '[data-formfield-error=recaptcha]'
        );
        // @email-hook
        this.isEmailHookAutofilled = false;
        this.isEmailHookDrawerOpen = 0;
        this.isQuickDebounceValidate = true;
    }

    /*
     * ocassional issue where formUI.js runs before agnosticFormController.js, so this function definition has been copied over to agnosticFormController to catch that edge case.
     * Please make sure both definitions are consistent
     */
    setFormHeight() {
        // @email-hook condition
        // Prevents form height from being set onto email-hook modal
        if (this.form.dataset['formName']?.includes('email_hook')) return;

        const formHeight = isNaN(parseInt(this.form.clientHeight))
            ? 'auto'
            : formConstants.errorMessageDefaultHeight + parseInt(this.form.clientHeight);
        this.form.style.minHeight =
            formHeight && formHeight !== 'auto' ? `${formHeight}px` : 'auto';
    }

    /* HIDING/SHOWING DRIFT LOGIC
     * if Drift loads before the user focuses on the form, we will not hide Drift
     * As soon as the user focuses on the form, we will insert a css class to hide Drift in the DOM (so that it won't steal focus)
     * As soon as the user leaves the form, we will show Drift (remove the class that hides Drift)
     * */
    driftDisplay(e) {
        const showDriftClass = 'js-show-drift';
        const hideDriftClass = 'js-hide-drift';
        if (e.target?.closest('form') === this.form) {
            if (!this.isChatOpen) {
                document.body.classList.add(hideDriftClass);
                document.body.classList.remove(showDriftClass);
            }
        } else {
            document.body.classList.remove(hideDriftClass);
            document.body.classList.add(showDriftClass);
        }
    }

    loadFormSpecs() {
        if (this.form) {
            document.body.addEventListener(
                'driftChatOpen',
                () => (this.isChatOpen = true)
            );

            document.addEventListener('mousedown', this.driftDisplay.bind(this));
            document.addEventListener('keydown', this.driftDisplay.bind(this));

            this.inputs = Array.from(this.form.querySelectorAll(this.inputSelector));
            if (this.recaptchaContainer) {
                loadCaptcha(this.form, 'formload');
            } else {
                this.form.classList.remove(this.hiddenFormClass);
            }
            this.setFormHeight(); /* just to avoid recurrent shifts in page height everytime the error message appears */
            handleZipCodeField(this.form);
            this.autofillBusinessTypeWithCookieValue();
            this.autofillCountryFieldWithCookieValue();
            this.autofillFormsWithLocalStorageData(this.inputs); // this should always be called BEFORE the phoneLibInit function
            /*
             * initPhoneLib() should always be called AFTER the autofill function. Because it affects the way that the
             * country gets pre-selected in the phone dropdown. If initPhoneLib() gets called before the autofill, the
             * selected country will be: either the country that the user is at OR the first country in the dropdown.
             * However, those 2 options might not correspond with the phone number saved in the local storage.
             * */
            try {
                [this.phoneLibInstance, this.phoneField] = initPhoneLib(this.form);
            } catch (e) {
                initPhoneLib(this.form).destroy();
                LoggingHandler().logError(e, {
                    searchTerm: 'phone-library-init-error',
                    tags: ['forms', 'ui'],
                    fileName: 'formUI.js',
                    additionalData: {
                        url: window.location.href,
                    },
                });
            }

            this.initFieldEvents(this.inputs);
            // @email-hook condition
            if (this.form.dataset['formName']?.includes('email_hook')) {
                this.addContinueButtonValidationListener();
            }
            this.raisePreFilledInputLabels();
            this.handleGooglePlacesFieldAutocomplete(
                'address',
                this.setDataAttributeForAddress
            );
            this.handleGooglePlacesFieldAutocomplete(
                'zip',
                this.setDataAttributeForZipCode
            );
            this.setDataAttributeForFieldsValidity();

            /* this is used for toggling display when the form first loads */
            handleDomainPrefixField({
                form: this.form,
            }).toggleDisplay();

            this.addListenerForDomainPrefixPrefilling();
        }
    }

    addListenerForDomainPrefixPrefilling() {
        if (this.form) {
            const companyField = this.form.querySelector('input[name=company]');
            const domainPrefixField = this.form.querySelector(
                'input[name=domain_prefix]'
            );
            if (companyField && domainPrefixField) {
                companyField.addEventListener('change', () => {
                    if (companyField.value.length >= 5 && !domainPrefixField.value) {
                        domainPrefixField.value = companyField.value
                            .replaceAll(/[^a-zA-Z0-9]/g, '') // remove every character that is not a letter
                            .toLowerCase();
                    }
                });
            }
        }
    }

    /**
     *
     * @param input
     * @param googlePlacesObjectsArray
     */
    //@todo this needs to be refactored (the function needs to be moved to its own file and included here and the logic needs to be separated into smaller functions). Also the setDataAttributeForZipCode()
    setDataAttributeForAddress(input, googlePlacesObjectsArray) {
        // This object's keys are specific to Kounta's platform
        // We are mapping Google's places api to this object
        // and providing a fallback (else if) in case the user entered the address manually
        let addressObj = { street: '', suburb: '', state: '', postcode: '' };
        const streetArr = [];
        if (googlePlacesObjectsArray) {
            googlePlacesObjectsArray.forEach(field => {
                if (field.types.includes('street_number') && field.long_name) {
                    streetArr.push(field.long_name);
                }
                if (field.types.includes('route') && field.long_name) {
                    streetArr.push(field.long_name);
                }
                if (streetArr.length > 0) {
                    addressObj.street = streetArr.join(', ');
                }
                if (field.types.includes('locality') && field.long_name) {
                    addressObj.suburb = field.long_name;
                }
                if (
                    field.types.includes('administrative_area_level_1') &&
                    field.long_name
                ) {
                    addressObj.state = field.long_name;
                }
                if (field.types.includes('postal_code') && field.long_name) {
                    addressObj.postcode = field.long_name;
                }
            });
        } else if (input.value) {
            addressObj.street = input.value;
        }
        // Set the data attribute with the stringified JSON as the value
        input.setAttribute('data-leadform-address', JSON.stringify(addressObj));
    }

    /**
     *
     * @param input
     * @param googlePlacesObjectsArray
     */
    setDataAttributeForZipCode(input, googlePlacesObjectsArray) {
        let zipCode = 'no-zipcode-provided';
        if (googlePlacesObjectsArray) {
            googlePlacesObjectsArray.forEach(field => {
                if (field.types.includes('postal_code')) {
                    /* As per google docs - https://developers.google.com/places/web-service/details#PlaceDetailsResults:
                     long_name is the full text description or name of the address component as returned by the Geocoder. Ex:
                     "long_name":"United States",
                     "short_name":"US"
                     */
                    /*There's no example provided for the postal code (or zipCode) long_name or short_name. I am sending the long_name anyway so that if it's applicable we will get as much details as possible.*/
                    zipCode = field.long_name;
                }
            });
        } else if (input.value) {
            zipCode = input.value;
        }
        input.setAttribute('data-leadform-zip', zipCode);
    }

    checkForPlaceDataAttribute(input, inputName) {
        return !input.hasAttribute(`data-leadform-${inputName}`);
    }

    /**
     *
     * @param inputName
     * @param callback
     */
    handleGooglePlacesFieldAutocomplete(inputName, callback) {
        const matchedInput = this.inputs.find(input => input.name === inputName);
        if (matchedInput) {
            if (
                inputName === 'zip' &&
                matchedInput.dataset.googlePlacesField !== 'true'
            ) {
                return;
            }
            const googlePlacesInstance = googlePlacesFieldAutocomplete(
                matchedInput,
                callback
            );

            // this is a fallback in case the field was autofilled, if the user used autofill this will be triggered
            // and the autofilled input text will be sent as is as the value of the street key in the data attribute object.
            matchedInput.addEventListener('blur', () => {
                if (this.checkForPlaceDataAttribute(matchedInput, inputName))
                    callback(matchedInput, null);
            });

            matchedInput.addEventListener('focus', () => googlePlacesInstance.init());
        }
    }

    /*
     * @param array, event, fn
     * */
    addEvents(array, event, fn) {
        if (typeof fn === 'function' && array.length > 0) {
            array.forEach(el => el.addEventListener(event, fn, false));
        }
    }

    /*
     * @param array
     * */
    initFieldEvents(array) {
        this.addEvents(array, 'focus', this.formValidationOnFocus.bind(this));
        this.addEvents(array, 'blur', this.formValidationOnChangeOrBlur.bind(this));
        this.addEvents(array, 'change', this.formValidationOnChangeOrBlur.bind(this));
        this.addEvents(array, 'keydown', this.formValidationWhileTyping.bind(this));

        // the domain prefix field has to be validated while typing, but on 'keyup' (when the user releases a key)
        this.addEvents(
            array,
            'keyup',
            this.domainPrefixFieldValidationWhileTyping.bind(this)
        );

        //listen for submit button click
        this.submitButton &&
            this.submitButton.addEventListener('click', e => {
                handleDomainPrefixField({
                    form: this.form,
                }).removeOnSubmit();
                honeyPotField(this.form, e);
                this.formValidationOnSubmit(e, this.inputs);
            });

        /* This has been commented out just to keep it handy in case we want to bring it back,
        we're not completely sure about the repercussions of removing the submit event on the form
        but the click event on the button functions exactly the same
        the reason we decided to remove it is because the data is getting submitted twice everytime.
        */
        // this.form.addEventListener('submit', e => {
        //     this.formValidationOnSubmit(e, this.inputs);
        // });

        /*
         * listen for invalid form fields and then
         * prevent the browser from showing default HTML5 error tooltip */
        document.addEventListener(
            'invalid',
            (function () {
                return function (e) {
                    e.preventDefault();
                };
            })(),
            true
        );
    }

    domainPrefixFieldValidationWhileTyping(e) {
        const isDomainPrefix = e.currentTarget.name === 'domain_prefix';
        if (isDomainPrefix) {
            this.hideUnavailableDomainMsg();
            event.target
                .closest(this.inputParentSelector)
                ?.classList.remove(this.validInputFieldClass);
            let countTiming;
            const iddleTypingTime = 1500;
            countTiming = setTimeout(() => {
                this.domainPrefixFieldValidation(e);
                clearTimeout(countTiming);
            }, iddleTypingTime);
        }
    }

    /* Sets a data attribute on the form which will update on blur and load (for if the fields have been pre-populated) */
    setDataAttributeForFieldsValidity() {
        this.form.setAttribute(
            'data-form-all-fields-valid',
            this.getInvalidInputs(this.inputs).length === 0
        );
        if (this.getInvalidInputs(this.inputs).length === 0) {
            this.form.dispatchEvent(this.fieldsValidationEvent);
        }
    }

    /*
     * On change or blur: hide errors, add errors if field invalid and raise field label
     * On blur: send event to Google Tag Manager
     * @param e
     * */
    formValidationOnChangeOrBlur(e) {
        e.preventDefault();
        this.hideErrorMessages([e.currentTarget.closest(this.inputParentSelector)]);
        if (!this.form.dataset['formName']?.includes('email_hook')) {
            this.removeAllWarningIcons([e.currentTarget]);
        }
        this.showErrorMessageIfInvalid(e.currentTarget);
        this.addWarningIconToInvalidFields([e.currentTarget]);
        this.addCheckMarkToValidField(e.currentTarget);
        this.raiseLabel(e.currentTarget);
        this.businessTypeFieldValidation(e);
        if (this.form.dataset['formName']?.includes('email_hook')) {
            this.validateEmailInput(e.currentTarget);
        }
        this.sendGoogleTagManagerFieldEvent(e);
        this.setDataAttributeForFieldsValidity();
        this.domainPrefixFieldValidation(e);
        this.checkboxValidationHandler(e);
    }

    checkboxValidationHandler(e) {
        if (e.currentTarget.type === 'checkbox') {
            if (e.currentTarget.checked) {
                this.showInputAsValid(e.currentTarget);
                this.hideCheckboxErrorMessageIfValid([e.currentTarget], e.type);
            } else {
                this.showCheckboxErrorMessageIfInvalid([e.currentTarget], e.type);
            }
        }
    }

    /*
     * @param e
     * */
    formValidationOnFocus(e) {
        e.preventDefault();
        this.hideErrorMessages(this.getFormFields());
        this.raiseLabel(e.currentTarget);
        if (e.currentTarget.value !== '') {
            // @email-hook condition
            if (!this.form.dataset['formName']?.includes('email_hook')) {
                this.removeAllWarningIcons([e.currentTarget]);
            }
            // @email-hook ends
            this.showErrorMessageIfInvalid(e.currentTarget);
            this.addWarningIconToInvalidFields([e.currentTarget]);
            this.addCheckMarkToValidField(e.currentTarget);
            this.validateEmailInput(e.currentTarget);
            this.validatePhoneInput(e.currentTarget);
        } else {
            if (e.currentTarget.classList.contains(this.inputErrorClass)) {
                this.showErrorMessageIfInvalid(e.currentTarget);
            }
        }
        this.domainPrefixFieldValidation(e);
        this.businessTypeFieldValidation(e);
    }

    async debounceValidate(e) {
        clearTimeout(debounceTimer);
        // @email-hook
        // Allows for quick validation on the first input change on any of these inputs
        // Added for the email hook modal, quick validates the email if a user selects it from an auto-complete dropdown. All subsequent keydown events follow slower timer duration (700ms)
        const timerDuration = this.isQuickDebounceValidate ? 10 : 700;
        this.isQuickDebounceValidate = false;
        // @email-hook ends
        const debounceTimer = setTimeout(async () => {
            if (e.target.name === 'phone') {
                if (this.phoneLibInstance?.isValidNumber()) {
                    this.addCheckMarkToValidField(e.target);
                }
            } else if (e.target.name === 'email') {
                const domain = e.target.value?.split('@')[1];
                if (e.target.checkValidity() === true && this.useEmailDomainValidation) {
                    const isValidDomain = await this.isValidEmailDomain(domain);
                    if (isValidDomain) {
                        this.addCheckMarkToValidField(e.target);
                        this.showInputAsValid(e.target);
                        // @email-hook condition
                        if (this.form.dataset['formName']?.includes('email_hook')) {
                            this.isEmailHookDrawerOpen++;
                            if (this.isEmailHookDrawerOpen === 1) {
                                const formGTMObjectConstructor =
                                    new FormGTMObjectConstructor(this.form);
                                GoogleTagManager.sendEvent(
                                    formGTMObjectConstructor.getFormGTMObject(
                                        'field_completed',
                                        getAnonymizedFieldValue(e.target),
                                        getFieldName(e.target)
                                    )
                                );
                                this.openEmailHookDrawer();
                            }
                        }
                        // @email-hook ends
                    } else {
                        this.showInputAsInvalid(e.target, true);
                    }
                }
            } else if (e.target.checkValidity()) {
                this.addCheckMarkToValidField(e.target);
            }
        }, timerDuration);
    }

    // @email-hook functions
    openEmailHookDrawer() {
        const emailHookModal = document.querySelector(
            '.js-leadform-email-hook__modal__form-content-wrapper'
        );
        if (emailHookModal) {
            this.form.style.height = `${this.form.scrollHeight}px`;
            emailHookModal.classList.remove(
                'js-leadform-email-hook__modal__form-content-wrapper--closed'
            );
            emailHookModal.classList.add(
                'js-leadform-email-hook__modal__form-content-wrapper--opened'
            );

            const disclaimerEl = emailHookModal.querySelector('.gb-form__disclaimer');
            if (disclaimerEl) disclaimerEl.style.display = 'block';
        }
        const allFields = Array.from(this.form.children);
        allFields.forEach(field => {
            field.removeAttribute('inert');
        });
    }

    addContinueButtonValidationListener() {
        const continueButton = document.querySelector(
            '.js-leadform-email-hook__modal__form-content-wrapper__continue-button'
        );
        const emailInput = this.form.querySelector('input[name=email]');
        const emailField = emailInput?.closest(this.inputParentSelector);
        continueButton.addEventListener('click', e => {
            e.preventDefault();
            this.raiseLabel(emailInput);
            this.validateEmailInput(emailInput);
            if (
                emailInput.checkValidity() &&
                emailField.classList.contains('js-field-valid')
            ) {
                this.openEmailHookDrawer();
            }
        });
    }
    // @email-hook ends

    /*
     * @param e
     * */
    formValidationWhileTyping(e) {
        const isDomainPrefix = e.target.name === 'domain_prefix';
        if (!isDomainPrefix) {
            this.hideErrorMessages([e.currentTarget.closest(this.inputParentSelector)]);
            this.removeAllWarningIcons([e.currentTarget], e.key);
            this.debounceValidate(e);
            this.businessTypeFieldValidation(e);
            this.setDataAttributeForFieldsValidity();
        }
    }

    /*
     * @param e, inputs
     * */
    formValidationOnSubmit(e, inputs) {
        const phoneInput = inputs.find(input => input.name === 'phone');
        phoneInput && this.validatePhoneInput(phoneInput);
        const regularBusinessTypeField = this.form.querySelector('[id^=business_type]');
        const agnosticBusinessTypeField = this.form.querySelector(
            '[id^=agnostic_business_type]'
        );
        const businessTypeField = regularBusinessTypeField
            ? regularBusinessTypeField
            : agnosticBusinessTypeField;
        businessTypeField && this.setBusinessTypeSlugField(businessTypeField);
        const emailInput = inputs.find(input => input.name === 'email');
        if (emailInput) {
            this.validateEmailInput(emailInput);
        }
        const recaptchaField = this.form.querySelector(
            'textarea[name=g-recaptcha-response]'
        );
        recaptchaField && recaptchaFormFieldValidation(this.form, e);
        const invalidInputs = this.getInvalidInputs(inputs);
        if (invalidInputs.length > 0) {
            e.preventDefault();
            // hide all error messages
            this.hideErrorMessages(this.getFormFields());
            // remove all warning icons
            this.removeAllWarningIcons(inputs);
            // add warning icon to invalid fields
            this.addWarningIconToInvalidFields(inputs);
            // display error message on first invalid field
            this.showErrorMessageIfInvalid(invalidInputs[0]);
            this.hideCheckboxErrorMessageIfValid(inputs, 'submit');
            this.showCheckboxErrorMessageIfInvalid(invalidInputs, 'submit');

            /*focus first invalid field so that the user
            doesn't have to move the cursor all the way there*/
            invalidInputs[0]?.focus();
            // raise invalid input labels
            invalidInputs.forEach(input => this.raiseLabel(input));
            this.businessTypeFieldValidation(e);
        } else {
            inputs.forEach(input => {
                if (input.name === 'phone') {
                    input.value = this.phoneLibInstance?.getNumber();
                }
            });
        }
    }

    isValidEmailDomain = async domain => {
        const endpoint = `https://dns.google/resolve?name=${domain}&type=mx`;
        const logErrorObject = {
            searchTerm: 'email-validation-error',
            tags: ['forms', 'ui'],
            fileName: 'formUI.js',
            additionalData: {
                url: window.location.href,
                domain,
            },
        };
        const data = await doFetch(endpoint, logErrorObject);
        const { Status } = data;
        return Status === 0;
    };

    /*
     * @param inputs
     * */
    removeAllWarningIcons(inputs, keyboardkey) {
        if (inputs && keyboardkey !== 'Enter') {
            inputs.forEach(input => {
                input.classList.remove(this.inputErrorClass);
                if (input.closest(this.inputParentSelector)) {
                    input
                        .closest(this.inputParentSelector)
                        .classList.remove(this.invalidInputFieldClass);
                }
            });
        }
    }

    /*
     * Adds the "!" warning icon to fields that contain the this.inputErrorClass
     * @param inputs
     * */
    addWarningIconToInvalidFields(inputs) {
        if (this.getInvalidInputs(inputs).length > 0) {
            this.getInvalidInputs(inputs).map(input => {
                input.classList.add(this.inputErrorClass);
                if (input.closest(this.inputParentSelector)) {
                    input
                        .closest(this.inputParentSelector)
                        .classList.add(this.invalidInputFieldClass);
                }
            });
        }
    }

    showCheckboxErrorMessageIfInvalid(inputs, eventType) {
        inputs.map(input => {
            if (input.type === 'checkbox') {
                LoggingHandler().logUserAction('SMS Optin was not checked', {
                    searchTerm: 'sms-optin-invalid',
                    tags: ['forms', 'sms_optin'],
                    fileName: 'formUI.js',
                    additionalData: {
                        eventType,
                    },
                });
                return input
                    .closest('[data-form-extras]')
                    ?.querySelector(this.errorMsgSelector)
                    ?.classList.add(this.showErrorMsgClass);
            }
        });
    }
    hideCheckboxErrorMessageIfValid(inputs, eventType) {
        inputs.map(input => {
            if (input.type === 'checkbox') {
                LoggingHandler().logUserAction('SMS Optin was checked', {
                    searchTerm: 'sms-optin-valid',
                    tags: ['forms', 'sms_optin'],
                    fileName: 'formUI.js',
                    additionalData: {
                        eventType,
                    },
                });
                return input
                    .closest('[data-form-extras]')
                    ?.querySelector(this.errorMsgSelector)
                    ?.classList.remove(this.showErrorMsgClass);
            }
        });
    }

    /**
     * @param input
     * @returns {null|Element|any}
     */
    getInputError(input) {
        const inputParent = input.closest(this.inputParentSelector);
        return inputParent?.querySelector(this.errorMsgSelector);
    }

    getEmailInputDomainError(input) {
        const inputParent = input.closest(this.inputParentSelector);
        return inputParent?.querySelector(this.emailDomainErrorMsgSelector);
    }

    /*
     * @param input
     * */
    showErrorMessageIfInvalid(input, isEmailDomain = false) {
        if (input) {
            if (
                input?.classList.contains(this.inputErrorClass) ||
                input.checkValidity() === false
            ) {
                if (isEmailDomain && this.getEmailInputDomainError(input)) {
                    this.getEmailInputDomainError(input) &&
                        this.getEmailInputDomainError(input)?.classList.add(
                            this.showErrorMsgClass
                        );
                    this.getInputError(input)?.classList.remove(this.showErrorMsgClass);
                } else {
                    this.getInputError(input)?.classList.add(this.showErrorMsgClass);
                    this.getEmailInputDomainError(input) &&
                        this.getEmailInputDomainError(input)?.classList.remove(
                            this.showErrorMsgClass
                        );
                }
            }
        }
    }

    /*
     * Hide error tooltips
     * @param fields
     * */
    hideErrorMessages(fields) {
        fields.map(field => {
            const fieldsWithError = field?.querySelectorAll(
                `${this.errorMsgSelector}, ${this.emailDomainErrorMsgSelector}`
            );

            fieldsWithError?.forEach(errorMessageEl => {
                errorMessageEl.classList.remove(this.showErrorMsgClass);
            });
        });
    }

    /*
     * Get invalid inputs, or inputs that contain the input error class
     * @param fields
     * */
    getInvalidInputs(inputs) {
        return Array.from(inputs).filter(
            input =>
                input.classList.contains(this.inputErrorClass) ||
                !input.validity.valid ||
                this.currentTargetIsADPBusinessType(input) ||
                (input === this.phoneField && !this.phoneLibInstance.isValidNumber()) ||
                this.hasInvalidDomainError(input)
        );
    }

    /*
     *  @returns array
     * */
    getFormFields() {
        return Array.prototype.slice.call(
            this.form.querySelectorAll(this.inputParentSelector)
        );
    }

    /*
     * Check if form is Retail free trial
     * @returns bool
     * */
    formIsRetailFreeTrial() {
        if (this.form.dataset['formName']) {
            return (
                this.form.dataset['formName'].includes('retail') &&
                this.form.dataset['formName'].includes('trial')
            );
        }
        return false;
    }

    /*
     * Check if form is eCommerce free trial
     * @returns bool
     * */
    formIsECommerceFreeTrial() {
        if (this.form.dataset['formName']) {
            return (
                this.form.dataset['formName'].toLowerCase().includes('ecom') &&
                this.form.dataset['formName'].includes('trial')
            );
        }
        return false;
    }

    /*
     * @param e
     * */
    businessTypeFieldValidation(e) {
        if (getFieldName(e.currentTarget) === 'business_type') {
            const parentElement = e.currentTarget.closest(this.inputParentSelector);
            const adpBusinessTypeError = parentElement
                ? parentElement.querySelector('[data-form=adp]')
                : '';
            if (
                adpBusinessTypeError &&
                this.form.dataset['formName'].includes('free_trial')
            ) {
                if (this.currentTargetIsADPBusinessType(e.currentTarget)) {
                    parentElement.classList.remove(this.validInputFieldClass);
                    adpBusinessTypeError.classList.add(this.showErrorMsgClass);
                    parentElement.classList.add(this.invalidInputFieldClass);
                    e.currentTarget.classList.add(this.inputErrorClass);
                } else {
                    adpBusinessTypeError.classList.remove(this.showErrorMsgClass);
                    parentElement.classList.remove(this.invalidInputFieldClass);
                    e.currentTarget.classList.remove(this.inputErrorClass);
                }
            }
            this.setBusinessTypeSlugField(e.currentTarget);

            handleDomainPrefixField({
                form: this.form,
                errorClass: this.inputErrorClass,
                target: e.currentTarget,
            }).toggleDisplay();
        }
    }

    domainPrefixShowValidationResult(e, isAvailable = false) {
        this.hideUnavailableDomainMsg();
        if (isAvailable) {
            this.hideDomainPrefixFieldErrors(e.target);
        } else {
            this.showDomainPrefixFieldErrors(e.target);
        }
    }

    hideDomainPrefixFieldErrors(field) {
        field.parentElement.classList.remove(this.invalidInputFieldClass);
        field.classList.remove(this.inputErrorClass);
        field.parentElement.classList.add(this.validInputFieldClass);
    }

    showDomainPrefixFieldErrors(field) {
        field.parentElement.classList.remove(this.validInputFieldClass);
        this.unavailableDomainMsg &&
            this.unavailableDomainMsg.classList.add(this.showErrorMsgClass);
        field.parentElement.classList.add(this.invalidInputFieldClass);
        field.classList.add(this.inputErrorClass);
    }

    hideUnavailableDomainMsg() {
        this.unavailableDomainMsg &&
            this.unavailableDomainMsg.classList.remove(this.showErrorMsgClass);
    }

    domainPrefixFieldValidation(e) {
        const eventTarget = e.currentTarget ? e.currentTarget : e.target;
        if (
            getFieldName(eventTarget) === 'domain_prefix' &&
            eventTarget &&
            eventTarget.value
        ) {
            this.hideErrorMessages([eventTarget.closest(this.inputParentSelector)]);
            this.removeAllWarningIcons([eventTarget]);
            if (eventTarget.checkValidity()) {
                this.domainPrefixAPICall(e);
            } else {
                this.showErrorMessageIfInvalid(eventTarget);
                this.addWarningIconToInvalidFields([eventTarget]);
            }
        }
    }

    domainPrefixAPICall = async inputEvent => {
        const myHeaders = new Headers();
        myHeaders.append('Content-Type', 'application/x-www-form-urlencoded');

        const urlencoded = new URLSearchParams();
        urlencoded.append('domain', inputEvent.target.value);

        const requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: urlencoded,
            cors: 'no-cors',
            redirect: 'follow',
        };

        const endpoint = '/rest/forms/load/domain-prefix-validation';

        return await doFetch(
            endpoint,
            this.getLogErrorObject(),
            this.checkDomainValidity.bind(this),
            null,
            requestOptions,
            [inputEvent]
        );
    };

    checkDomainValidity(responseData, event) {
        if (responseData.valid && responseData.available) {
            this.domainPrefixShowValidationResult(event, true);
        } else {
            this.domainPrefixShowValidationResult(event, false);
        }
    }

    hasInvalidDomainError(input) {
        if (input.name === 'domain_prefix') {
            const unavailableDomainError = input
                .closest(this.inputParentSelector)
                ?.querySelector('[data-form=unavailable-domain-error]');
            return (
                unavailableDomainError &&
                unavailableDomainError.classList.contains(this.showErrorMsgClass)
            );
        }

        return false;
    }

    getLogErrorObject() {
        return {
            searchTerm: 'domain-prefix-validation-error',
            tags: ['forms', 'ui'],
            fileName: 'formUI.js',
            additionalData: {
                url: window.location.href,
            },
        };
    }
    showInputAsValid(input) {
        input.closest(this.inputParentSelector).classList.add(this.validInputFieldClass);
        input
            .closest(this.inputParentSelector)
            .classList.remove(this.invalidInputFieldClass);
        input.classList.remove(this.inputErrorClass);
    }

    showInputAsInvalid(input, isEmailDomain = false) {
        input
            .closest(this.inputParentSelector)
            .classList.add(this.invalidInputFieldClass);
        input
            .closest(this.inputParentSelector)
            .classList.remove(this.validInputFieldClass);
        input.classList.add(this.inputErrorClass);
        if (isEmailDomain) {
            this.showErrorMessageIfInvalid(input, isEmailDomain);
        } else {
            this.showErrorMessageIfInvalid(input);
        }
    }

    validateEmailInput(emailInput) {
        if (getFieldName(emailInput) === 'email') {
            if (emailInput.checkValidity() === true) {
                if (this.useEmailDomainValidation) {
                    const domain = emailInput.value?.split('@')[1];
                    const domainValidityPromise = this.isValidEmailDomain(domain);
                    domainValidityPromise
                        .then(isValid => {
                            if (isValid) {
                                this.showInputAsValid(emailInput);
                            } else {
                                LoggingHandler().logUserAction(
                                    'User entered an invalid email domain',
                                    {
                                        searchTerm: 'email-field-domain-invalid',
                                        tags: ['forms', 'email_domain_validation'],
                                        fileName: 'formUI.js',
                                        additionalData: {
                                            url: window.location.href,
                                            isValid,
                                        },
                                    }
                                );
                                this.showInputAsInvalid(emailInput, true);
                            }
                        })
                        .catch(e => {
                            LoggingHandler().logError('Email Validation Error', {
                                searchTerm: 'email-domain-fetch-error',
                                tags: ['forms', 'email_domain_validation'],
                                fileName: 'formUI.js',
                                additionalData: {
                                    error: e,
                                    url: window.location.href,
                                },
                            });
                        });
                }
            } else {
                LoggingHandler().logUserAction('User entered an invalid email syntax', {
                    searchTerm: 'email-field-syntax-invalid',
                    tags: ['forms', 'email_syntax'],
                    fileName: 'formUI.js',
                    additionalData: {
                        url: window.location.href,
                    },
                });
                this.showInputAsInvalid(emailInput);
            }
        }
    }

    validatePhoneInput(phoneInput) {
        if (getFieldName(phoneInput) === 'phone') {
            if (this.phoneLibInstance.isValidNumber()) {
                this.showInputAsValid(phoneInput);
            } else {
                this.showInputAsInvalid(phoneInput);
            }
        }
    }

    /*
     * This returns true if the current event target is a SELECT tag and if its selected <option> tag has the 'data-adp' attribute
     * @param e
     * @returns bool
     * */
    currentTargetIsADPBusinessType(currentInput) {
        return (
            (this.formIsRetailFreeTrial() || this.formIsECommerceFreeTrial()) &&
            currentInput.tagName === 'SELECT' &&
            currentInput.options[currentInput.selectedIndex].hasAttribute('data-adp')
        );
    }

    /* The field that gets set here is referred to in this file html/src/php/Pages/ThankYou/Request.php
    this is part of the X & K series launch, where we show different watch a demo video depending on the chosen business type
    https://jira.atlightspeed.net/browse/MW-4673 */
    setBusinessTypeSlugField(businessTypeField) {
        if (this.getCurrentTargetSlug(businessTypeField)) {
            const businessTypeSlugField = this.form.querySelector(
                'input[name=business_type_slug]'
            );
            if (businessTypeSlugField) {
                businessTypeSlugField.value =
                    this.getCurrentTargetSlug(businessTypeField);
            }
        }
    }

    getCurrentTargetSlug(currentInput) {
        return currentInput.tagName === 'SELECT' &&
            currentInput.options[currentInput.selectedIndex]?.hasAttribute('data-slug')
            ? currentInput.options[currentInput.selectedIndex].dataset.slug
            : '';
    }

    /*
     * @param e
     * */
    sendGoogleTagManagerFieldEvent(e) {
        if (
            e.type === 'change' &&
            !e.currentTarget.classList.contains(this.inputErrorClass)
        ) {
            gtm.field(this.form).sendEvent(
                `${getFieldName(e.currentTarget)}|${getAnonymizedFieldValue(
                    e.currentTarget
                )}`
            );
            const formGTMObjectConstructor = new FormGTMObjectConstructor(this.form);
            GoogleTagManager.sendEvent(
                formGTMObjectConstructor.getFormGTMObject(
                    'field_completed',
                    getAnonymizedFieldValue(e.currentTarget),
                    getFieldName(e.currentTarget)
                )
            );
        }
    }

    /*
     * Loops through business type option tags that contain the `data-slug` attribute
     * compares the value of data-slug with the business types cookie.
     * If they are equal, it sets the business type value to the currently cookied business type
     * return {void}
     * */
    autofillBusinessTypeWithCookieValue() {
        const businessTypeCookie = this.cm.getCookie('ls_vertical_bt');
        const businessTypeField = this.form.querySelector('[id^=business_type]');
        const businessTypeOptionsWithSlug = businessTypeField
            ? businessTypeField.querySelectorAll('[data-slug]')
            : null;

        if (businessTypeCookie && businessTypeOptionsWithSlug) {
            const businessTypeOption = Array.prototype.slice
                .call(businessTypeOptionsWithSlug)
                .find(
                    businessTypeOption =>
                        businessTypeOption.dataset.slug === businessTypeCookie
                );
            if (businessTypeOption) {
                businessTypeField.value = businessTypeOption.value;
                businessTypeField.parentElement.classList.add(this.validInputFieldClass);
            }
        }
    }

    /*
     * Get initial country from geoConfig
     * */
    autofillCountryFieldWithCookieValue = () => {
        const countrySelect = this.form.querySelector('[id$=user_set_country_code]');
        const countryCookie =
            this.cm.getCookie('geoConfig')?.country_code?.toLowerCase() ?? 'us';
        if (countrySelect !== null) {
            countrySelect.value = countryCookie.toUpperCase();
        }
    };

    /*
     * @param fields
     * */
    autofillFormsWithLocalStorageData(fields) {
        let isFormAutoFilled = false;
        if (!isFormExcludedFromLocalStorage(this.form) && isEnabledFormAutofill()) {
            fields.forEach(field => {
                const localStorageFieldValue = getDecodedFormFieldValue(
                    getFieldName(field)
                );
                if (localStorageFieldValue) {
                    isFormAutoFilled = true;
                    field.value = localStorageFieldValue;
                    // @email-hook condition
                    if (this.form.dataset['formName']?.includes('email_hook')) {
                        const formGTMObjectConstructor = new FormGTMObjectConstructor(
                            this.form
                        );
                        GoogleTagManager.sendEvent(
                            formGTMObjectConstructor.getFormGTMObject(
                                'field_completed',
                                getAnonymizedFieldValue(field),
                                getFieldName(field)
                            )
                        );
                        if (field.name === 'email') this.openEmailHookDrawer();
                    }
                    // @email-hook ends
                    if (field.value) {
                        field
                            .closest(this.inputParentSelector)
                            .classList.add(this.validInputFieldClass);
                    }
                }
            });
            this.sendGTMFormAutofillEvent(isFormAutoFilled);
        }
    }

    /*
     * @param isFormAutoFilled
     * */
    sendGTMFormAutofillEvent(isFormAutoFilled) {
        if (isFormAutoFilled) {
            gtm.form(this.form).sendEvent('autocomplete');
        }
    }

    raisePreFilledInputLabels() {
        const inputsWithValue = this.inputs.filter(input => input.value);
        inputsWithValue.forEach(input => this.raiseLabel(input));
    }

    /*
     * @param input
     * */
    raiseLabel(input) {
        // hack to prevent an empty select field with a default value from having its label raised
        if (input.value === 'default-role-field') return;

        const label = input
            ?.closest(this.inputParentSelector)
            ?.querySelector('.gb-form__field-label');
        const raisedLabelClass = 'js-raised';

        // @email-hook condition
        // prevent raising label on inputs that don't have placeholder text (therefore there is no reason to raise the label, it will never be displaced by the placeholder text)
        if (
            this.form.dataset['formName']?.includes('email_hook') &&
            !input.parentElement.parentElement.classList.contains('js-field-valid') &&
            !input.placeholder
        ) {
            if (label) label.classList.remove(raisedLabelClass);
            return;
        }
        // @email-hook ends

        if (label) label.classList.add(raisedLabelClass);
    }

    /*
     * @param e
     * */
    addCheckMarkToValidField(el) {
        /* The domain prefix field has 2 levels of validation:
            1. HTML validation
            2. API call to a vend endpoint that checks if the domain is available and not already taken
            The condition bellow runs when an HTML validation gets triggered. We want for the green checkmark that
            indicates that the user entered a valid value to only show up when both levels of validation (HTML & API)
            have passed. Excluding the domain prefix field from the condition bellow prevents it from showing a checkmark
            too early, before the domain availability check has happened.
        */
        const isDomainPrefixField = el?.name === 'domain_prefix';
        if (el.checkValidity() === true) {
            !isDomainPrefixField &&
                el
                    ?.closest(this.inputParentSelector)
                    ?.classList.add(this.validInputFieldClass);
        } else {
            el?.closest(this.inputParentSelector)?.classList.remove(
                this.validInputFieldClass
            );
        }
    }
}
